import React, {useCallback, useMemo} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import {downloadReportCsv} from '../../services/reportService';
import {useTranslation} from 'react-i18next';
import {useResizeDetector} from 'react-resize-detector';
import {
  smallAxisStyleAxis,
  smallAxisStyleLabel,
  smallAxisStyleTickLabels,
  smallAxisStyleTicks,
  toolTipStyle,
} from '../../services/graphService';
import {DateTime} from 'luxon';
import GraphLegendItem from '../shared/GraphLegendItem';
import {PositionGroup} from '../../enums';
import {ORANGE_COLOR, SKY_BLUE, VIVID_VIOLET} from '../../styles';

const getPositionColor = (position: PositionGroup) => {
  switch (position) {
    case PositionGroup.all:
      return ORANGE_COLOR;
    case PositionGroup.defense:
      return VIVID_VIOLET;
    case PositionGroup.forwards:
      return SKY_BLUE;
    default:
      return '#fff';
  }
};

const CHART_COLORS = [
  '#FFD700', // Gold
  '#DC143C', // Crimson
  '#00FF7F', // Spring Green
  '#6A0DAD', // Purple
  '#FFBF00', // Amber
  '#00BFFF', // Deep Sky Blue
  '#FF00A6', // Hot Pink
  '#32CD32', // Lime Green
  '#FF00FF', // Magenta
  '#007FFF', // Azure
  '#FF6F61', // Coral
  '#39FF14', // Neon Green
  '#9B111E', // Dark Red
  '#30D5C8', // Turquoise
  '#4B0082', // Indigo
  '#E6A800', // Dark Gold
  '#FF69B4', // Pink
  '#4169E1', // Royal Blue
  '#7FFF00', // Chartreuse
  '#00AEEF', // Sky Blue
];

export interface PlayerDayData {
  firstName?: string;
  lastName?: string;
  position?: PositionGroup;
  data: {
    date: string;
    value: number;
  }[];
  trend?: {
    percentChange?: number;
  };
}

interface Props {
  data: PlayerDayData[];
  minDate?: string;
  maxDate?: string;
  metricName: string;
  metricUnit?: string;
  metricDecimalPlaces?: number;
  teamName: string;
  graphType?: 'line' | 'bar';
  xAxisTickPeriod?: number;
}

const TeamPlayerMetricsGraph: React.FC<Props> = ({
  data,
  minDate,
  maxDate,
  metricName,
  metricUnit,
  metricDecimalPlaces = 1,
  teamName,
  graphType = 'line',
  xAxisTickPeriod = 7,
}) => {
  const {t} = useTranslation();
  const {width, ref} = useResizeDetector();

  const isPositionGraph = useMemo(
    () =>
      data.every(
        player =>
          player.position !== undefined &&
          !player.firstName &&
          !player.lastName,
      ),
    [data],
  );

  const onDownload = useCallback(() => {
    const flatData = data
      .flatMap(player =>
        player.data.map(item => {
          if (isPositionGraph) {
            return {
              Position: t(`position.${player.position}`),
              Date: item.date,
              [`${metricName}${metricUnit ? ` (${metricUnit})` : ''}`]:
                item.value,
            };
          }
          return {
            'First Name': player.firstName,
            'Last Name': player.lastName,
            Date: item.date,
            [`${metricName}${metricUnit ? ` (${metricUnit})` : ''}`]:
              item.value,
          };
        }),
      )
      .sort((a, b) => {
        // sort by date then by position
        if (a.Date === b.Date) {
          return (a.Position ?? '').localeCompare(b.Position ?? '');
        }
        return a.Date.localeCompare(b.Date);
      });
    downloadReportCsv({
      exportReportData: flatData,
      teamName,
      reportName: metricName,
    });
  }, [data, isPositionGraph, metricName, metricUnit, t, teamName]);

  const xAxisValues = useMemo(() => {
    const minDateCalculated =
      minDate ??
      data.flatMap(player => player.data.map(item => item.date)).sort()[0];
    const maxDateCalculated =
      maxDate ??
      data.flatMap(player => player.data.map(item => item.date)).sort()[
        data.flatMap(player => player.data.map(item => item.date)).length - 1
      ];

    const dates = [];
    let date = minDateCalculated;
    while (date <= maxDateCalculated) {
      dates.push(date);
      date = DateTime.fromFormat(date, 'yyyy-MM-dd')
        .plus({days: 1})
        .toFormat('yyyy-MM-dd');
    }
    return dates;
  }, [data, minDate, maxDate]);

  const getTooltipText = useCallback(
    (date: string, firstName?: string, lastName?: string) => {
      if (isPositionGraph) {
        const all = (
          data.find(player => player.position === PositionGroup.all)?.data ?? []
        ).find(item => item.date === date);
        const forwards = (
          data.find(player => player.position === PositionGroup.forwards)
            ?.data ?? []
        ).find(item => item.date === date);
        const defense = data
          .find(player => player.position === PositionGroup.defense)
          ?.data.find(item => item.date === date);
        const toolTipSegments = [
          `${DateTime.fromISO(date).toFormat('M/d')}`,
          all &&
            `${t('position.all')}: ${all?.value.toFixed(metricDecimalPlaces)}${metricUnit ? ` ${metricUnit}` : ''}`,
          forwards &&
            `${t('position.forwards')}: ${forwards?.value.toFixed(metricDecimalPlaces)}${metricUnit ? ` ${metricUnit}` : ''}`,
          defense &&
            `${t('position.defense')}: ${defense?.value.toFixed(metricDecimalPlaces)}${metricUnit ? ` ${metricUnit}` : ''}`,
        ];
        return toolTipSegments.filter(t => t).join('\n');
      } else {
        // find all players with activity on this date
        const playersWithActivity = data.filter(player =>
          player.data.find(item => item.date === date),
        );
        // we only want to show tooltip for first player with activity on this date
        if (
          playersWithActivity[0]?.firstName === firstName &&
          playersWithActivity[0]?.lastName === lastName
        ) {
          const toolTipSegments = [
            `${DateTime.fromISO(date).toFormat('M/d')}`,
            ...data
              .sort((a, b) => {
                const aValue = a.data.find(item => item.date === date)?.value;
                const bValue = b.data.find(item => item.date === date)?.value;
                if (aValue && bValue) {
                  return aValue > bValue ? -1 : 1;
                }
                return 0;
              })
              .map(player => {
                if (player.data.find(item => item.date === date)) {
                  return `${player.firstName} ${player.lastName}: ${player.data.find(item => item.date === date)?.value.toFixed(metricDecimalPlaces)}${metricUnit ? ` ${metricUnit}` : ''}`;
                }
                return '';
              }),
          ];
          return toolTipSegments.filter(t => t).join('\n');
        }
        return '';
      }
    },
    [data, isPositionGraph, metricUnit, t, metricDecimalPlaces],
  );

  return (
    <Box sx={{width: '100%', mb: 5}} ref={ref}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}>
        <Typography variant="h3">{metricName}</Typography>
        <Button onClick={onDownload}>{t('general.download')}</Button>
      </Box>
      <VictoryChart
        width={width}
        containerComponent={
          <VictoryVoronoiContainer
            voronoiDimension="x"
            labels={({datum}) => {
              return getTooltipText(datum.x, datum.firstName, datum.lastName);
            }}
            labelComponent={
              <VictoryTooltip style={toolTipStyle} constrainToVisibleArea />
            }
            voronoiBlacklist={
              isPositionGraph ? ['forwards-line', 'defense-line'] : []
            }
          />
        }>
        {graphType === 'bar' && (data ?? []).length > 0 && (
          <VictoryGroup offset={5}>
            {data.map((player, index) => (
              <VictoryBar
                key={`${player.firstName}-${player.lastName}-${player.position}-bar`}
                data={player.data.map(date => ({
                  firstName: player.firstName,
                  lastName: player.lastName,
                  position: player.position,
                  date: date.date,
                  value: date.value,
                  x: date.date,
                  y: date.value,
                }))}
                barWidth={5}
                style={{
                  data: {
                    fill: isPositionGraph
                      ? getPositionColor(player.position as PositionGroup)
                      : CHART_COLORS[index % CHART_COLORS.length],
                  },
                }}
              />
            ))}
          </VictoryGroup>
        )}
        {graphType === 'line' &&
          (data ?? []).length > 0 &&
          data.map((player, index) => (
            <VictoryLine
              key={`${player.firstName?.length ? `${player.firstName}-` : ''}${player.lastName?.length ? `${player.lastName}-` : ''}${player.position?.length ? `${player.position}-` : ''}line`}
              name={`${player.firstName?.length ? `${player.firstName}-` : ''}${player.lastName?.length ? `${player.lastName}-` : ''}${player.position?.length ? `${player.position}-` : ''}line`}
              data={player.data.map(date => ({
                firstName: player.firstName,
                lastName: player.lastName,
                position: player.position,
                date: date.date,
                value: date.value,
                x: date.date,
                y: date.value,
              }))}
              style={{
                data: {
                  stroke: isPositionGraph
                    ? getPositionColor(player.position as PositionGroup)
                    : CHART_COLORS[index % CHART_COLORS.length],
                  strokeWidth: 2,
                },
              }}
            />
          ))}
        {graphType === 'line' &&
          false &&
          (data ?? []).length > 0 &&
          data.map((player, index) => (
            <VictoryScatter
              key={`${player.firstName}-${player.lastName}-${player.position}-line`}
              data={player.data.map(date => ({
                firstName: player.firstName,
                lastName: player.lastName,
                position: player.position,
                date: date.date,
                value: date.value,
                x: date.date,
                y: date.value,
              }))}
              size={2}
              style={{
                data: {
                  stroke: isPositionGraph
                    ? getPositionColor(player.position as PositionGroup)
                    : CHART_COLORS[index % CHART_COLORS.length],
                  strokeWidth: 1,
                  fill: isPositionGraph
                    ? getPositionColor(player.position as PositionGroup)
                    : CHART_COLORS[index % CHART_COLORS.length],
                },
              }}
            />
          ))}
        <VictoryAxis
          tickValues={xAxisValues}
          tickFormat={(tick, index) =>
            index % xAxisTickPeriod === 0
              ? DateTime.fromISO(tick).toFormat('M/d')
              : null
          }
          style={{
            axis: smallAxisStyleAxis,
            ticks: smallAxisStyleTicks,
            tickLabels: smallAxisStyleTickLabels,
          }}
        />
        <VictoryAxis
          dependentAxis
          orientation="left"
          offsetX={70}
          label={metricUnit}
          tickFormat={t => `${t}`}
          style={{
            axis: smallAxisStyleAxis,
            ticks: smallAxisStyleTicks,
            tickLabels: smallAxisStyleTickLabels,
            axisLabel: smallAxisStyleLabel,
          }}
        />
      </VictoryChart>
      <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
        {data.map((player, index) => (
          <GraphLegendItem
            key={
              isPositionGraph
                ? player.position
                : `${player.firstName}-${player.lastName}`
            }
            label={
              isPositionGraph
                ? `${t(`position.${player.position}`)} ${player.trend?.percentChange ? `(${player.trend.percentChange > 0 ? '+' : ''}${player.trend.percentChange.toFixed(1)}%)` : ''}`
                : `${player.firstName} ${player.lastName} ${player.trend?.percentChange ? `(${player.trend.percentChange > 0 ? '+' : ''}${player.trend.percentChange.toFixed(1)}%)` : ''}`
            }
            backgroundColor={
              isPositionGraph
                ? getPositionColor(player.position as PositionGroup)
                : CHART_COLORS[index % CHART_COLORS.length]
            }
            sx={{mr: 2}}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TeamPlayerMetricsGraph;
